import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Theme,
  Typography,
  createStyles,
  makeStyles
} from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmDeleteBoxProps {
  open: boolean;
  text?: string;
  confirmText?: string;
  important?: boolean;
  deleteForever?: boolean;
  onDelete?: () => void;
  onClose?: () => void;
  children?: React.ReactNode;
}

export default function ConfirmDeleteBox({
  open = false,
  text,
  confirmText = 'DELETE',
  important = false,
  deleteForever = false,
  onDelete,
  onClose,
  children
}: ConfirmDeleteBoxProps) {
  const { t } = useTranslation();
  const displayText = text || t('common.thisItem');
  const [textFromUser, setTextFromUser] = useState<string>('');
  const showError = confirmText !== textFromUser;

  const classes = useStyles({ showError });

  function onChangeTextField(event: React.ChangeEvent<HTMLInputElement>) {
    setTextFromUser(event.target.value);
  }

  function handleClose() {
    setTimeout(() => {
      setTextFromUser('');
    }, 200);
    if (onClose) onClose();
  }

  function handleDelete() {
    setTimeout(() => {
      setTextFromUser('');
    }, 200);
    if (onDelete) onDelete();
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        {deleteForever ? t('confirmDeleteBox.titles.deleteForever') : t('confirmDeleteBox.titles.permanentlyDelete')}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {deleteForever ? (
          <Typography color="textSecondary">
            {t('confirmDeleteBox.descriptions.deleteForever', { itemName: displayText })}
          </Typography>
        ) : (
          <Typography color="textSecondary">
            {t('confirmDeleteBox.descriptions.permanentlyDelete', { itemName: displayText })}
          </Typography>
        )}
        {children}
        {important && (
          <>
            <Divider className={classes.divider} />
            {t('confirmDeleteBox.instruction')}{' '}
            <Box className={classes.confirmText} component="b">
              {confirmText}
            </Box>
            <TextField
              className={classes.textFieldConfirm}
              id="confirm-delete-text-field"
              size="small"
              variant="outlined"
              placeholder={t('confirmDeleteBox.placeholder', { confirmText }) as string}
              error={showError}
              helperText={
                showError
                  ? t('confirmDeleteBox.errorText')
                  : ''
              }
              value={textFromUser}
              onChange={onChangeTextField}
              fullWidth
            />
          </>
        )}
      </DialogContent>
      <Box className={classes.dialogActions}>
        <Button
          className={classes.deleteButton}
          color="primary"
          variant="contained"
          onClick={handleDelete}
          disabled={important && confirmText !== textFromUser}
          fullWidth
        >
          {deleteForever ? t('confirmDeleteBox.buttons.deleteForever') : t('confirmDeleteBox.buttons.delete')}
        </Button>
        <Button color="inherit" onClick={handleClose} fullWidth>
          {t('confirmDeleteBox.buttons.cancel')}
        </Button>
      </Box>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: { paddingBottom: theme.spacing(2) },
    divider: { margin: theme.spacing(1, 0, 2, 0) },
    confirmText: { whiteSpace: 'pre-wrap' },
    textFieldConfirm: (props: { showError: boolean }) => ({
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderColor: props.showError
            ? theme.palette.error.main
            : theme.palette.text.primary
        }
      }
    }),
    deleteButton: {
      backgroundColor: theme.palette.error.main,
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: theme.palette.error.dark
      }
    },
    dialogActions: {
      display: 'flex',
      padding: theme.spacing(1),
      gap: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column-reverse'
      }
    }
  })
);
